'use client';

import { Typography } from 'components';
import { FALLBACK_GRAVATAR } from 'constants/images';
import { useTranslations } from 'next-intl';

import Image, { ImageLoaderProps } from 'next/image';
import Link from 'next/link';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { FC, ReactNode } from 'react';

const BLUR_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAAXNSR0IArs4c6QAAATtJREFUKFM9kDtLA0EUhc+d3ewiCRpFJYWC0UJFESuxEgRBBLG1sBIEf4hC9D9oGYymFjSNIjbZYBchW4gPAoH4XAlu3N2ZkZmNYeDOvczHuecM5U7LAkQEAKoSdAupjtQtIKWk3JmjRwWFkUDIhX6zTAbTYF1Ygwr6DTky/UlMjw4oAdy/fODN82ElYpgOihUZhBzZTC82lyaRMJhWbIccJ9c11N9bsExDgY4MIoHtlRmMDKYQiXi1yRgeGl/IX9VgWya6HnfX5pBO2v/ulWs0vR8cXVZhMgIdFiuyHUTYWJzA/PgQRCcqI0LZbeDi7hk9SlGtVkHUsLM621V99Xwcl6qIuNSBaL/gyHTKxvpCVns04i8FFxJPzW+cO49otUPQXv7W31qeorHhPgQR73iMLzthwK1/onDjyj8PmIe5q7cdOQAAAABJRU5ErkJggg==';

interface IAccountLayout {
  client: ReactNode;
  emailNotifications: ReactNode;
  timezone: ReactNode;
  language: ReactNode;
}

const AccountLayout: FC<IAccountLayout> = ({ client, timezone, emailNotifications, language }) => {
  const t = useTranslations();
  const { data } = useCurrentUser();

  const imageLoader = ({ src, width }: ImageLoaderProps) => {
    return `https://gravatar.com/avatar/${src}?d=${FALLBACK_GRAVATAR}&s=${width || 75}`;
  };

  return (
    <div className="flex gap-6 py-8">
      <div className="w-3/12">
        <div className="flex flex-col items-center justify-center">
          <Image
            loader={imageLoader}
            alt="user-img"
            height={400}
            width={400}
            src={data?.gravatarEmailHash ?? ''}
            priority
            blurDataURL={BLUR_DATA_URL}
            placeholder="blur"
          />
          <Link target="_blank" href={'https://gravatar.com/emails'}>
            <Typography variant="medium4" className=" text-center text-secondary-link">
              <div className="mt-4 text-center">
                {t('account_details.profile_info_form.edit_photo_on_gravatar')}
              </div>
            </Typography>
          </Link>
        </div>
      </div>
      <div className="flex w-9/12 flex-col gap-6 ">
        <div className="w-full rounded-md bg-white p-4 shadow-custom">{client}</div>
        <div className="w-full rounded-md bg-white p-4 shadow-custom">
          <Typography variant="regular1" className="text-tertiary-100">
            {t('account_details.general.settings')}
          </Typography>
          <div className="flex gap-4">
            {timezone}
            {language}
          </div>
          {emailNotifications}
        </div>
      </div>
    </div>
  );
};

export default AccountLayout;
